import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_STEPS } from '@/hooks/contest/contestSteps.js';
import { triggerConfetti } from "@/utils/animations.js";
import { ExternalLink, Instagram, Facebook, Star } from 'lucide-react';
import { useBrandConfig } from "@/features/brandDemo/BrandConfigContext.jsx";
import { BrandLogo } from "@/components/brandDemo/BrandLogo.jsx";

const CDN = "https://cdn.prod.website-files.com/64e75b79eaa8eb84e0f6f6d7";
const BG_PHOTO = `${CDN}/64f764f5cda901ea21ae4fd1_photo-marquee-1-p-800.jpg`;

const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
);

const SOCIAL_ICONS = {
    tiktok:    <TikTokIcon />,
    instagram: <Instagram size={20} strokeWidth={2} />,
    facebook:  <Facebook size={20} strokeWidth={2} />,
};

export const GruppomimoStepResult = ({
    status,
    reward,
    pseudo,
    triggerAnimation = true,
}) => {
    const { t } = useTranslation();
    const { brand, result, demoContest } = useBrandConfig();

    const [isVisible, setIsVisible] = useState(false);
    const isWinner = status === APP_STEPS.RESULT_WIN;

    const googlePlaceId = demoContest?.place?.googlePlaceId;
    const googleReviewUrl = googlePlaceId
        ? `https://search.google.com/local/writereview?placeid=${googlePlaceId}`
        : null;

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 150);
        if (isWinner && triggerAnimation) {
            triggerConfetti(result.confettiColors);
        }
        return () => clearTimeout(timer);
    }, [isWinner, triggerAnimation, result.confettiColors]);

    const customStyles = `
        .gm-stagger-1 { transition-delay: 80ms; }
        .gm-stagger-2 { transition-delay: 180ms; }
        .gm-stagger-3 { transition-delay: 280ms; }
        .gm-stagger-4 { transition-delay: 380ms; }

        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        @keyframes gm-stamp {
            0%   { transform: scale(2.5) rotate(-15deg); opacity: 0; }
            60%  { transform: scale(0.92) rotate(3deg);  opacity: 1; }
            100% { transform: scale(1)   rotate(0deg);   opacity: 1; }
        }
        .animate-gm-stamp { animation: gm-stamp 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }

        @keyframes gm-pulse-ring {
            0%   { transform: scale(1);   opacity: 0.4; }
            100% { transform: scale(1.6); opacity: 0; }
        }
        .animate-gm-pulse-ring { animation: gm-pulse-ring 1.6s ease-out infinite; }

        /* Bord perforé du ticket */
        .ticket-perf {
            background-image: radial-gradient(circle at 0 50%, transparent 12px, white 12px),
                              radial-gradient(circle at 100% 50%, transparent 12px, white 12px);
        }
    `;

    return (
        <div className="absolute inset-0 z-50 font-body overflow-hidden flex flex-col" style={{ backgroundColor: brand.colors.pageBg }}>
            <style dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* ── HERO PHOTO ─────────────────────────────────────────── */}
            <div className="relative shrink-0 h-[10%] overflow-hidden">
                <img src={BG_PHOTO} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
                <div
                    className="absolute inset-0"
                    style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, ${brand.colors.pageBg} 100%)` }}
                />
                {/* Logo */}
                <div className={`absolute bottom-2 left-0 right-0 flex justify-center transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                    <BrandLogo className="h-20 w-auto object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]" />
                </div>
            </div>

            {/* ── CONTENU ────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto pb-36 hide-scrollbar px-5">
                <div className="flex flex-col items-center max-w-sm mx-auto pt-2 gap-4">

                    {/* TITRE */}
                    <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} gm-stagger-1`}>
                        <p className="text-[11px] font-black uppercase tracking-[0.25em] mb-1" style={{ color: `${brand.colors.accent}99` }}>
                            {isWinner ? '🏆 Félicitations !' : '🍽️ Merci d\'avoir joué !'}
                        </p>
                        <h1
                            className="font-heading text-[3rem] uppercase italic tracking-tighter leading-none"
                            style={{ color: brand.colors.accent }}
                        >
                            {isWinner ? 'Tu as\ngagné !' : 'Un cadeau\npour toi !'}
                        </h1>
                    </div>

                    {/* ── TICKET ─────────────────────────────────────── */}
                    <div className={`w-full transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} gm-stagger-2`}>
                        <div className="bg-white rounded-[2rem] shadow-[0_16px_48px_-8px_rgba(0,0,0,0.15)] overflow-hidden">

                            {/* Haut du ticket — fond brand */}
                            <div
                                className="relative flex flex-col items-center px-6 pt-7 pb-6"
                                style={{ backgroundColor: brand.colors.accent }}
                            >
                                {/* Icône succès animée */}
                                <div className="relative flex items-center justify-center mb-4">
                                    <div
                                        className="absolute w-16 h-16 rounded-full animate-gm-pulse-ring"
                                        style={{ backgroundColor: 'rgba(255,255,255,0.4)' }}
                                    />
                                    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg animate-gm-stamp">
                                        <span className="text-2xl">{isWinner ? '🍽️' : '🍮'}</span>
                                    </div>
                                </div>
                                <p className="text-white/80 text-[10px] font-black uppercase tracking-[0.25em] mb-1">
                                    {isWinner ? 'Votre récompense' : 'Lot de consolation'}
                                </p>
                                <h2 className="text-white font-heading text-[2.2rem] uppercase italic tracking-tight leading-tight text-center">
                                    {reward?.title ?? (isWinner ? 'Repas pour 2 offert' : 'Tiramisu offert')}
                                </h2>
                            </div>

                            {/* Séparation perforée */}
                            <div className="relative flex items-center px-4 py-0 bg-white">
                                <div className="absolute -left-3 w-6 h-6 rounded-full" style={{ backgroundColor: brand.colors.pageBg }} />
                                <div className="flex-1 border-t-2 border-dashed" style={{ borderColor: `${brand.colors.accent}30` }} />
                                <div className="absolute -right-3 w-6 h-6 rounded-full" style={{ backgroundColor: brand.colors.pageBg }} />
                            </div>

                            {/* Bas du ticket — instruction */}
                            <div className="flex flex-col items-center px-6 py-6 gap-3 bg-white">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                                    Comment l'utiliser ?
                                </p>
                                <div className="flex items-start gap-3 w-full bg-gray-50 rounded-2xl p-4">
                                    <span className="text-xl mt-0.5">📱</span>
                                    <p className="text-[12px] font-bold text-gray-700 leading-snug">
                                        Montrez simplement cet écran à votre serveur lors de l'addition.
                                    </p>
                                </div>
                                <p className="text-[9px] font-bold text-gray-300 uppercase tracking-widest text-center">
                                    Valable ce soir uniquement · Non cumulable
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── GOOGLE REVIEWS ─────────────────────────────── */}
                    {googleReviewUrl && (
                        <div className={`w-full transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} gm-stagger-3`}>
                            <a
                                href={googleReviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col items-center gap-3 w-full bg-white rounded-[2rem] p-5 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] border border-gray-100 active:scale-[0.98] transition-transform"
                            >
                                <div className="flex items-center gap-2">
                                    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                    </svg>
                                    <span className="text-[12px] font-black text-gray-600 uppercase tracking-wider">Avis Google</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    {[1,2,3,4,5].map((i) => (
                                        <svg key={i} viewBox="0 0 24 24" fill="#FBBC05" className="w-8 h-8">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                    ))}
                                </div>
                                <div className="text-center">
                                    <p className="font-heading text-xl font-black uppercase italic tracking-tight text-gray-900">
                                        Laisser un avis
                                    </p>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                        Ça nous aide beaucoup !
                                    </p>
                                </div>
                            </a>
                        </div>
                    )}

                    {/* ── SOCIALS ────────────────────────────────────── */}
                    {result.socials?.length > 0 && (
                        <div className={`w-full transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} gm-stagger-4`}>
                            <div className="bg-white/60 backdrop-blur-sm border border-white/80 rounded-[2rem] p-5 text-center shadow-sm">
                                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400 mb-3">
                                    Suivez-nous
                                </p>
                                <div className="flex items-center justify-center gap-3">
                                    {result.socials.map(({ platform, url }) => (
                                        <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                                            className="w-12 h-12 bg-white rounded-full flex items-center justify-center transition-all shadow-sm border border-gray-100 hover:scale-110 active:scale-95"
                                            style={{ color: brand.colors.accent }}
                                        >
                                            {SOCIAL_ICONS[platform] ?? null}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* ── CTA SITE WEB ───────────────────────────────────────── */}
            <div
                className={`absolute bottom-0 left-0 w-full px-5 pb-6 pt-20 z-30 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ background: `linear-gradient(to top, ${brand.colors.pageBg} 60%, transparent)` }}
            >
                <div className="max-w-sm mx-auto">
                    <a
                        href={brand.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group w-full flex items-center justify-center gap-3 py-4 rounded-full font-heading text-xl uppercase italic tracking-wider shadow-lg active:translate-y-1 transition-all"
                        style={{ backgroundColor: brand.colors.accent, color: brand.colors.accentText }}
                    >
                        Reserver en ligne
                        <ExternalLink size={20} strokeWidth={2.5} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                </div>
            </div>
        </div>
    );
};
